import { useEffect, useState } from "react";
import { Pencil, Trash2, UserPlus } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import { Card, LoadingBlock } from "@/components/common";
import { MultiSelect } from "@/components/MultiSelect";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const ROLE_HELP = { admin: "Everything, including settings", manager: "Dashboards, edit complaints, upload", viewer: "Read-only" };

const UserDialog = ({ user, onClose, onSaved }) => {
  const { meta } = useApp();
  const [f, setF] = useState({ email: user?.email || "", name: user?.name || "", role: user?.role || "manager", brands: user?.brands || [], active: user?.active !== false, password: "" });
  const save = async () => {
    try {
      const body = { ...f, password: f.password || null };
      user ? await api.put(`/users/${user.id}`, body) : await api.post("/users", body);
      toast.success("User saved");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="user-dialog">
        <DialogHeader><DialogTitle>{user ? "Edit user" : "Add a user"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2"><Label>Name</Label><Input data-testid="user-name-input" className="h-11 rounded-xl" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></div>
          <div className="space-y-2"><Label>Email</Label><Input data-testid="user-email-input" type="email" autoComplete="off" autoCapitalize="none" className="h-11 rounded-xl" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></div>
          <div className="space-y-2"><Label>{user ? "New password (leave blank to keep)" : "Password"}</Label><Input data-testid="user-password-input" type="text" name="new-user-password" autoComplete="off" autoCapitalize="none" autoCorrect="off" spellCheck={false} placeholder={user ? "" : "At least 6 characters — share this with the user"} className="h-11 rounded-xl font-mono" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} /></div>
          <div className="space-y-2"><Label>Role</Label>
            <Select value={f.role} onValueChange={(v) => setF({ ...f, role: v })}>
              <SelectTrigger className="h-11 rounded-xl" data-testid="user-role-select"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(ROLE_HELP).map(([r, h]) => <SelectItem key={r} value={r}><span className="capitalize">{r}</span> — {h}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-2"><Label>Brands this user can see</Label>
            <div><MultiSelect label="Brands" options={meta.brands} value={f.brands} onChange={(v) => setF({ ...f, brands: v })} testid="user-brands-select" /></div>
            <p className="text-xs text-slate-500">"All" means every brand.</p>
          </div>
          <label className="flex items-center gap-3 text-sm"><Switch checked={f.active} onCheckedChange={(v) => setF({ ...f, active: v })} data-testid="user-active-switch" />Can sign in</label>
          <Button className="h-11 w-full rounded-xl" onClick={save} data-testid="user-save-button">Save user</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default function UsersTab() {
  const [users, setUsers] = useState(null);
  const [dlg, setDlg] = useState(null);
  const { user: me } = useAuth();
  const remove = async (u) => {
    if (!window.confirm(`Delete ${u.name} (${u.email})? They will no longer be able to sign in. This cannot be undone.`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("User deleted");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const load = () => api.get("/users").then((r) => setUsers(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => {
    load();
  }, []);
  if (!users) return <LoadingBlock />;
  return (
    <Card title="Users" subtitle="Only people you add here can sign in." testid="users-tab" right={<Button className="rounded-xl" onClick={() => setDlg({})} data-testid="add-user-button"><UserPlus className="mr-2 h-4 w-4" />Add user</Button>}>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="border-b text-left text-xs uppercase text-slate-400"><th className="py-2">Name</th><th>Email</th><th>Role</th><th>Brands</th><th>Status</th><th /></tr></thead>
          <tbody>{users.map((u) => (
            <tr key={u.id} className="border-b border-slate-100" data-testid={`user-row-${u.email}`}>
              <td className="py-3 pr-3 font-medium">{u.name}</td><td className="pr-3 text-slate-600">{u.email}</td><td className="pr-3 capitalize">{u.role}</td>
              <td className="pr-3 text-slate-600">{u.brands?.length ? u.brands.join(", ") : "All"}</td>
              <td className="pr-3">{u.active !== false ? <span className="text-emerald-600">Active</span> : <span className="text-slate-400">Disabled</span>}</td>
              <td className="whitespace-nowrap text-right"><Button size="icon" variant="ghost" onClick={() => setDlg({ user: u })} data-testid={`edit-user-${u.email}`}><Pencil className="h-4 w-4" /></Button>
                {u.id !== me?.id && <Button size="icon" variant="ghost" className="text-red-500 hover:bg-red-50 hover:text-red-600" onClick={() => remove(u)} data-testid={`delete-user-${u.email}`}><Trash2 className="h-4 w-4" /></Button>}</td>
            </tr>
          ))}</tbody>
        </table>
      </div>
      {dlg && <UserDialog user={dlg.user} onClose={() => setDlg(null)} onSaved={load} />}
    </Card>
  );
}
